import { createStore } from 'zustand';
import type { ProductCard } from '@repo/supergraph/roundup/base/types';

export type CompareOffersStoreState = {
  selectedOffers: ProductCard[];
  maxSelectedOffers: number;
  isComparisonOpen: boolean;
  /** The list view's `SKU Viewed` impression id for each currently- (or previously-) selected
   * offer, keyed by offer id, so components downstream of selection (footer, table, thumbnail
   * remove) can attribute analytics back to that offer's original list-view impression. */
  skuImpressionIdsByOfferId: Record<string, string>;
  toggleOfferSelection: (offer: ProductCard, skuImpressionId?: string) => void;
  clearSelection: () => void;
  openComparison: () => void;
  closeComparison: () => void;
};

export function createCompareOffersStore(maxSelectedOffers: number) {
  return createStore<CompareOffersStoreState>()((set, get) => ({
    selectedOffers: [],
    maxSelectedOffers,
    isComparisonOpen: false,
    skuImpressionIdsByOfferId: {},
    toggleOfferSelection: (offer: ProductCard, skuImpressionId?: string) => {
      const { selectedOffers, isComparisonOpen, skuImpressionIdsByOfferId } = get();
      const offerId = offer.marketplaceEntity.id;
      const isSelected = selectedOffers.some(
        selectedOffer => selectedOffer.marketplaceEntity.id === offerId,
      );
      if (isSelected) {
        const nextSelectedOffers = selectedOffers.filter(
          selectedOffer => selectedOffer.marketplaceEntity.id !== offerId,
        );
        set({
          selectedOffers: nextSelectedOffers,
          isComparisonOpen: nextSelectedOffers.length === 0 ? false : isComparisonOpen,
        });
        return;
      }
      if (selectedOffers.length >= maxSelectedOffers) return;
      set({
        selectedOffers: [...selectedOffers, offer],
        skuImpressionIdsByOfferId: skuImpressionId
          ? { ...skuImpressionIdsByOfferId, [offerId]: skuImpressionId }
          : skuImpressionIdsByOfferId,
      });
    },
    clearSelection: () => set({ selectedOffers: [], isComparisonOpen: false }),
    openComparison: () => set({ isComparisonOpen: true }),
    closeComparison: () => set({ isComparisonOpen: false }),
  }));
}

export type CompareOffersStore = ReturnType<typeof createCompareOffersStore>;
